import { comparatorDobError, isUnder18On, localTodayIso } from '@/utils/patientDob'

/** Shown when email/phone are disabled because the comparator is under 18. */
export const MINOR_COMPARATOR_CONTACT_HELPER = 'Contact details are not collected for a comparator under 18.'

export type ComparatorContactFields = {
  dateOfBirth: string
  email: string
  phone: string
}

/**
 * Single contact-entry policy for a comparator DOB.
 * - `open`: valid adult DOB — email/phone editable and may be required
 * - `disabledPendingDob`: empty/partial/invalid DOB — fields disabled
 *   (`contactFieldsDisabled`), keep values, no minor helper; callers omit
 *   contactChannel requiredness via the same disabled predicate
 * - `blockedMinor`: confirmed under-18 — fields disabled (`contactFieldsDisabled`),
 *   strip contact, show helper; callers omit contactChannel requiredness via the
 *   same disabled predicate
 */
export type ComparatorContactPolicy = 'open' | 'disabledPendingDob' | 'blockedMinor'

export function comparatorContactPolicy(
  dateOfBirth: string,
  todayIso: string = localTodayIso()
): ComparatorContactPolicy {
  if (comparatorDobError(dateOfBirth)) return 'disabledPendingDob'
  if (isUnder18On(dateOfBirth.trim(), todayIso)) return 'blockedMinor'
  return 'open'
}

/** True for every non-`open` policy — UI disable and contactChannel requiredness share this. */
export function contactFieldsDisabled(policy: ComparatorContactPolicy): boolean {
  return policy !== 'open'
}

export function contactHelperVisible(policy: ComparatorContactPolicy): boolean {
  return policy === 'blockedMinor'
}

/** Clear email/phone once policy is `blockedMinor`. Partial/empty DOB keeps values. */
export function applyComparatorContactPolicy<T extends ComparatorContactFields>(
  draft: T,
  todayIso: string = localTodayIso()
): T {
  if (comparatorContactPolicy(draft.dateOfBirth, todayIso) !== 'blockedMinor') return draft
  if (draft.email === '' && draft.phone === '') return draft
  return { ...draft, email: '', phone: '' }
}
